<script setup lang="ts">
import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import api from '~/api';

const props = defineProps<{
  sourceImages: string[];
  isUploading?: boolean;
  /** 上传/裁剪/选素材带过来的图片比例，用来自动选中左侧 Ratio */
  presetAspect?: string;
}>();
const emit = defineEmits<{
  (e: 'result', payload: { url: string; type: 'image' }): void;
  (e: 'state', s: { canGenerate: boolean; label: string; costLabel: string; busy: boolean }): void;
}>();
const { t } = useI18n();

// Prompt 模块只对付费用户开放（历史付费 or 订阅生效），与服务端
// isPaidUserFromRecord 对齐；免费用户连 prompt 都拿不到（接口已剥掉）
const comm = useCommStore();
const isPaidUser = computed(() => {
  const u = comm.userInfo;
  return !!u && ((u.counter_payment_lists ?? 0) > 0 || u.sub_status === 1);
});

interface Effect { id: string; name: string; thumbnail: string; prompt?: string | null }

const effects = ref<Effect[]>([]);
const selectedEffect = ref('');
const aspect = ref<'1:1' | '3:4' | '4:3' | '9:16' | '16:9' | '2:3' | '3:2'>('3:4');
const photoreal = ref(false);
const ultraDetail = ref(false);
const customPrompt = ref('');

// 选中 effect 的 prompt：选中/切换 effect 时预填进文本框供用户微调；
// Reset 恢复成该 effect 默认 prompt（与 vidsoda UndressClient 一致）
const selectedEffectPrompt = computed(() => {
  const e = effects.value.find((x) => x.id === selectedEffect.value);
  return (e?.prompt ?? '').trim();
});
const canResetPrompt = computed(
  () => !!selectedEffectPrompt.value && customPrompt.value.trim() !== selectedEffectPrompt.value,
);
function resetPrompt() {
  customPrompt.value = selectedEffectPrompt.value;
}
watch(selectedEffect, () => { customPrompt.value = selectedEffectPrompt.value; });

const submitting = ref(false);
const polling = ref(false);
const errorMsg = ref('');
const loadingEffects = ref(true);

const ASPECT_OPTIONS: (typeof aspect.value)[] = ['1:1', '3:4', '4:3', '9:16', '16:9', '2:3', '3:2'];

// 按比例真实绘制的小方框（最长边 16px），替代不准的 unicode 矩形字符
function ratioBox(id: string) {
  const [w, h] = id.split(':').map(Number);
  const max = 16;
  return {
    width: `${w >= h ? max : Math.round((max * w) / h)}px`,
    height: `${h >= w ? max : Math.round((max * h) / w)}px`,
  };
}

const cost = computed(() => {
  let c = 4;
  if (photoreal.value) c += 4;
  if (ultraDetail.value) c += 4;
  return c;
});
const canGenerate = computed(() =>
  !submitting.value && !polling.value && !props.isUploading && props.sourceImages.length > 0,
);

// 上传/裁剪/选素材后，按图片比例自动选中左侧 Ratio（用户仍可手动改，
// 最终输出以这里选中的比例为准）
watch(
  () => props.presetAspect,
  (v) => {
    if (v && ASPECT_OPTIONS.includes(v as typeof aspect.value)) aspect.value = v as typeof aspect.value;
  },
  { immediate: true },
);

// 选中比例 → 出图 width/height。上游 /generate-qwen-rapid-aio 用 width/height
// （不接受比例字符串），不传则被服务端兜底成正方形 → 之前结果全是 1:1。
// 以长边为基准、按比例推短边：HD 长边 1024（3:4 → 768×1024，正好是上游
// 文档默认值），FHD 长边 1280（3:4 → 960×1280）。范围 64–2048（文档约束），
// 对齐到 64 的倍数。
function ratioToSize(aspectStr: string, fhd: boolean) {
  const [w, h] = aspectStr.split(':').map(Number);
  const longEdge = fhd ? 1280 : 1024;
  const snap = (n: number) => Math.max(64, Math.min(2048, Math.round(n / 64) * 64));
  return w >= h
    ? { width: snap(longEdge), height: snap((longEdge * h) / w) }
    : { width: snap((longEdge * w) / h), height: snap(longEdge) };
}

onMounted(async () => {
  loadingEffects.value = true;
  try {
    const res = await $fetch<{ code: number; data: { effects: Effect[] } }>(
      '/api/effects/list',
      { method: 'POST', body: { tool: 'sex-pose' } },
    );
    effects.value = res.data.effects;
    if (effects.value.length) {
      selectedEffect.value = effects.value[0].id;
      customPrompt.value = selectedEffectPrompt.value;
    }
  } catch (e) {
    console.error('load effects failed', e);
  } finally {
    loadingEffects.value = false;
  }
});

async function generate() {
  if (props.isUploading) {
    errorMsg.value = t('editor.imageUploading');
    return;
  }
  if (props.sourceImages.length === 0) {
    errorMsg.value = t('editor.uploadFirst');
    return;
  }
  errorMsg.value = '';
  submitting.value = true;
  try {
    const eff = effects.value.find((e) => e.id === selectedEffect.value);
    // customPrompt 已预填 effect prompt 并可被用户微调；后端以用户 prompt 为准
    const { width, height } = ratioToSize(aspect.value, ultraDetail.value);
    const r = await api.createSexPose({
      image_urls: props.sourceImages,
      effect_id: eff?.id,
      prompt: customPrompt.value.trim() || undefined,
      photoreal: photoreal.value,
      ultra_detail: ultraDetail.value,
      quality: ultraDetail.value ? 'FHD' : 'HD',
      width,
      height,
    });
    submitting.value = false;
    polling.value = true;

    let attempts = 0;
    const timer = setInterval(async () => {
      attempts++;
      try {
        const res = await api.fetchSexPoseRes(r.order_id, r.task_id);
        if (res.task_status === 1) {
          emit('result', { url: res.url, type: 'image' });
          clearInterval(timer); polling.value = false;
        } else if (res.task_status === -1 || attempts >= 60) {
          errorMsg.value = res.message || t('editor.generationFailed');
          clearInterval(timer); polling.value = false;
        }
      } catch {/* ignore */}
    }, 6000);
  } catch (e: any) {
    submitting.value = false;
    errorMsg.value = e?.message || t('editor.submissionFailed');
  }
}

// 把生成按钮状态镜像给父级（editor.vue 在上传区下面/移动端底部放一个
// 同样触发生成的桥接按钮，逻辑仍全部留在本组件内）
watchEffect(() => {
  emit('state', {
    canGenerate: canGenerate.value,
    label: submitting.value
      ? t('editor.submitting')
      : polling.value
        ? t('editor.generating')
        : t('editor.generate'),
    costLabel: t('editor.creditsCount', { n: cost.value }),
    busy: submitting.value || polling.value,
  });
});
defineExpose({ generate });
</script>

<template>
  <div class="panel">
    <div class="head">
      <h2>{{ t('editor.effect') }}</h2>
    </div>

    <!-- 效果网格（可滚动） -->
    <div class="grid-wrap">
      <div v-if="loadingEffects" class="grid">
        <div v-for="i in 9" :key="i" class="skeleton" />
      </div>
      <div v-else-if="!effects.length" class="empty">{{ t('editor.noEffects') }}</div>
      <template v-else>
        <div class="grid">
          <button
            v-for="e in effects" :key="e.id"
            class="card"
            :class="{ active: selectedEffect === e.id }"
            @click="selectedEffect = e.id"
          >
            <img :src="e.thumbnail" :alt="e.name" loading="lazy" />
            <span class="card-label">{{ e.name }}</span>
          </button>
        </div>
        <div v-if="effects.length > 6" class="scroll-hint">{{ t('editor.scrollMore') }}</div>
      </template>
    </div>

    <!-- 高级选项 -->
    <div class="toggles">
      <label class="toggle">
        <span class="toggle-label"><span class="crown">♛</span> {{ t('editor.photoreal') }}</span>
        <input type="checkbox" v-model="photoreal" />
        <span class="switch" />
      </label>
      <label class="toggle">
        <span class="toggle-label"><span class="crown">♛</span> {{ t('editor.ultraDetail') }}</span>
        <input type="checkbox" v-model="ultraDetail" />
        <span class="switch" />
      </label>
    </div>

    <!-- Ratio -->
    <div class="section">
      <h3>{{ t('editor.ratio') }}</h3>
      <div class="ratio-grid">
        <button
          v-for="a in ASPECT_OPTIONS" :key="a"
          class="ratio-btn"
          :class="{ active: aspect === a }"
          @click="aspect = a"
        >
          <span class="ratio-box" :style="ratioBox(a)" />
          <span>{{ a }}</span>
        </button>
      </div>
    </div>

    <!-- Prompt：选中 effect 预填，可微调（仅付费用户可见） -->
    <div v-if="isPaidUser" class="section">
      <div class="prompt-head">
        <h3>{{ t('editor.prompt') }}</h3>
        <button
          v-if="canResetPrompt"
          type="button"
          class="reset-prompt"
          @click="resetPrompt"
        >
          {{ t('editor.resetPrompt') }}
        </button>
      </div>
      <textarea v-model="customPrompt" rows="3" :placeholder="t('editor.posePromptPlaceholder')" />
    </div>

    <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
  </div>
</template>

<style scoped lang="scss">
.panel {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-5 space-y-4 text-white;
}
.head h2 { @apply text-lg font-bold; }

.grid-wrap {
  position: relative;
  max-height: 520px;
  overflow-y: auto;
  border-radius: 0.75rem;
  background: rgba(10, 10, 10, 0.5);
}
.grid-wrap::-webkit-scrollbar { width: 6px; }
.grid-wrap::-webkit-scrollbar-thumb { background: rgba(251, 191, 36, 0.4); border-radius: 3px; }
.scroll-hint {
  position: sticky;
  bottom: 0;
  text-align: center;
  font-size: 0.7rem;
  color: rgba(251, 191, 36, 0.7);
  padding: 0.5rem 0 0;
  background: linear-gradient(to bottom, transparent, rgba(10, 10, 10, 0.95));
  pointer-events: none;
}
.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}
.card {
  @apply relative aspect-[3/4] rounded-lg overflow-hidden border-2 border-transparent transition cursor-pointer;
  &.active { @apply border-amber-500; box-shadow: 0 0 12px rgba(251,191,36,0.35); }
  img { @apply size-full object-cover; }
}
.card-label {
  @apply absolute bottom-1 left-1 right-1 text-xs font-medium text-white/95 bg-black/40 rounded px-1.5 py-0.5 text-center;
}
.skeleton {
  @apply aspect-[3/4] rounded-lg;
  background: linear-gradient(90deg,
    rgba(64,64,64,0.3) 0%,
    rgba(96,96,96,0.4) 50%,
    rgba(64,64,64,0.3) 100%);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.empty {
  @apply text-center text-sm text-neutral-500 py-12;
}

.toggles { @apply space-y-2 pt-2; }
.toggle {
  @apply flex items-center justify-between rounded-xl bg-neutral-950/60 border border-neutral-800 px-4 py-3 cursor-pointer;
  input { @apply hidden; }
}
.toggle-label { @apply text-sm font-medium flex items-center gap-2; }
.crown { @apply text-amber-400; }
.switch {
  @apply w-10 h-6 rounded-full bg-neutral-700 relative transition;
  &::after {
    content: '';
    @apply absolute size-5 rounded-full bg-white top-0.5 left-0.5 transition-all;
  }
}
.toggle input:checked + .switch { @apply bg-amber-500; }
.toggle input:checked + .switch::after { @apply left-[1.125rem]; }

.section { @apply pt-2 space-y-2; }
.section h3 { @apply text-sm font-semibold; }
.opt { @apply text-xs text-neutral-500 font-normal ml-1; }
.prompt-head { @apply flex items-center justify-between; }
.reset-prompt { @apply text-xs text-amber-400 hover:text-amber-300 transition; }

.ratio-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.ratio-btn {
  @apply flex flex-row items-center justify-center gap-1.5 rounded-full bg-neutral-950/60 border border-neutral-800 py-2 text-xs transition;
  &.active { @apply bg-white text-neutral-900 border-white; }
}
.ratio-box {
  display: inline-block;
  flex: none;
  border: 1.5px solid currentColor;
  border-radius: 2px;
  opacity: 0.8;
}
.ratio-btn.active .ratio-box { opacity: 1; }

.section textarea {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 resize-y;
}

.cta {
  @apply flex items-center justify-between gap-2 w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all
    disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249,115,22,0.5);
}
.spark { @apply text-amber-200; }
.cta-text { @apply flex-1 text-left; }
.cost { @apply rounded-full bg-black/30 px-3 py-0.5 text-xs; }
.err { @apply text-sm text-red-400; }
</style>
